import * as React from 'react'
import { observer, inject } from 'mobx-react'
import { CreateProduct } from '../ProductStore'
import { CurrentCompanyStore } from '../../../../../stores/CurrentCompanyStore'
import { AsyncNameSearchInput } from '../../../../../components/Input/AsyncSearch/AsyncNameSearchInput'
import { AsyncSKUSearchInput } from '../../../../../components/Input/AsyncSearch/AsyncSKUSearchInput'
import { IBaseProduct, IBaseCategory, IPricingGroup } from '@getgreenline/homi-shared'
import { message, Icon, Select } from 'antd'
import { Routes } from '../../../../../constants/Routes'

import { PricingGroupHelpModal } from '../Components/PricingGroupHelpModal'
import { PricingGroupMigrationModal } from '../Components/PricingGroupMigrationModal'
import { getCategoryName, filterOption, GrRegex } from '../../../../../utilities/helpers'
import TextArea from 'antd/lib/input/TextArea'
import { Switch } from 'antd-v4'
import { ProductEventLogs } from '../ProductEventLogs'
import { BlazeButton } from '../../../../../components/CustomButtons/BlazeButton'

interface Props {
  product: CreateProduct
  pricingGroups: IPricingGroup[]
  currentCompanyStore?: CurrentCompanyStore
  saveProductFirstWarning: JSX.Element
  isEditMode: boolean
  searchDuplicateSkus: (inputSku: string, productResults: IBaseProduct[]) => void
  blockSaving: (blockSaving: boolean, errorMessage?: string) => void
}

interface State {
  isRefreshing: boolean
  showPricingGroupHelpModal: boolean
  showPricingGroupMigrationModal: boolean
  showEditPurchaseCostModal: boolean
  isOverrideWeightedAverage: boolean
  viewEditHistory: boolean
}

@inject('currentCompanyStore')
@observer
export class BasicInformationSection extends React.Component<Props, State> {
  currentCompanyStore = this.props.currentCompanyStore!

  state = {
    isRefreshing: false,
    showPricingGroupHelpModal: false,
    showPricingGroupMigrationModal: false,
    showEditPurchaseCostModal: false,
    isOverrideWeightedAverage: false,
    viewEditHistory: false,
  }

  componentDidMount = () => {
    // Retrieve pricing groups
    const productStore = this.props.currentCompanyStore!.productStore!

    // Set taxgroups in ProductStore class
    productStore.getTaxGroups()
  }

  updateCategories = async () => {
    this.setState({ isRefreshing: true })

    try {
      await this.props.currentCompanyStore!.productStore!.getCategories()
      message.success('Categories successfully updated')
    } catch (error) {
      message.error('There was an error while updating categories')
    } finally {
      this.setState({ isRefreshing: false })
    }
  }

  updateSuppliers = async () => {
    this.setState({ isRefreshing: true })
    try {
      await this.props.currentCompanyStore!.supplierStore!.getSuppliers()
      message.success('Vendors successfully updated')
    } catch (error) {
      message.error('There was an error while updating vendors')
    } finally {
      this.setState({ isRefreshing: false })
    }
  }

  handleChildProductsOnPricingGroupIdChange = (
    product: CreateProduct,
    pricingGroupId: string | null,
  ) => {
    // If a pricing group was set, empty out child products
    if (pricingGroupId) {
      product.childProducts.forEach((childProduct, index) => {
        product.removeChildProduct(index)
      })
    } else {
      // If a pricing group was unset, add a child product
      product.addChildProduct()
    }
  }

  attachPricingGroup = (pricingGroupId: string | null) => {
    const { product } = this.props
    product.setPricingGroupId(pricingGroupId)

    // Initial add page (not edit)
    if (!product.id) {
      this.handleChildProductsOnPricingGroupIdChange(product, pricingGroupId)
    }

    // needsPricingGroupRefresh is used to determine how to display variants
    if (product.needsPricingGroupRefresh) {
      this.setState({ showPricingGroupMigrationModal: true })
    }
  }

  onPricingGroupMigrationModalClose = () => {
    const { product } = this.props

    // if modal is closed but not saved, set pricing group id to null
    product.setPricingGroupId(null)
    product.childProducts.forEach((cp) => {
      cp.setPricingGroupId(null)
    })

    this.setState({ showPricingGroupMigrationModal: false })
  }

  updateTaxGroups = async () => {
    this.setState({ isRefreshing: true })

    try {
      await this.props.currentCompanyStore!.productStore!.getTaxGroups()
      message.success('Tax groups successfully updated')
    } catch (error) {
      message.error('There was an error while updating tax groups')
    } finally {
      this.setState({ isRefreshing: false })
    }
  }

  autoEnableBatchTrack = async () => {
    const { product, isEditMode } = this.props
    const company = this.currentCompanyStore!.company

    if (company && company.isBatchTracking && company.province === 'AB' && !isEditMode) {
      if (await this.isComplianceCategoryAssigned(product.categoryId)) {
        product.setIsBatchTracked(true)
      } else {
        product.setIsBatchTracked(false)
      }
    }
  }

  isComplianceCategoryAssigned = async (categoryId: string | null) => {
    const categories = await this.currentCompanyStore.productStore!.flattenedCategories
    const categorySelected: IBaseCategory | undefined = categories.find(
      (category) => category.id === categoryId,
    )
    return categorySelected && categorySelected!.complianceCategoryId ? true : false
  }

  render() {
    const { product, searchDuplicateSkus, pricingGroups } = this.props
    const taxGroups = this.props.currentCompanyStore!.productStore!.taxGroups || []
    const { isRefreshing, showPricingGroupHelpModal, showPricingGroupMigrationModal } = this.state

    const { company, productStore, supplierStore } = this.props.currentCompanyStore!

    const flattenedCategories = productStore!.filteredFlatCategories || []
    const suppliers = supplierStore!.suppliersWithName || []
    const hasVariants = product.childProducts.length > 0
    return (
      <>
        <div className='card mb-4'>
          <div className='card-header d-flex'>
            <div style={{ flexGrow: 1 }}>Basic information</div>
            <div className='blaze-text-muted'>
              {product.isActive ? 'Active (shows up on POS)' : 'Inactive (will not show up on POS)'}

              <Switch
                className='ml-2'
                checked={product.isActive}
                disabled={product.isDeleted}
                onChange={() => product.setIsActive(!product.isActive)}
              />
            </div>
          </div>
          <div className='card-body'>
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>{hasVariants ? 'Parent SKU' : 'SKU'}</label>
                {hasVariants && <div className='blaze-text-muted'>Optional</div>}
              </div>
              <div className='col-lg-6'>
                <AsyncSKUSearchInput
                  value={product.sku || ''}
                  disabled={product.isDeleted}
                  searchDuplicateSkus={searchDuplicateSkus}
                  onChange={(sku) => product.setSKU(sku)}
                />
              </div>
            </div>
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>
                  Name<em className='blaze-text-danger'> * required</em>
                </label>
                <div className='blaze-text-muted'>Ex. Purple Kush</div>
              </div>
              <div className='col-lg-6'>
                <AsyncNameSearchInput
                  productId={product.id}
                  value={product.name || ''}
                  disabled={product.isDeleted}
                  onChange={(name) => product.setName(GrRegex.replaceLatin1Space(name))}
                  blockSaving={this.props.blockSaving}
                />
              </div>
            </div>
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>Category</label>
                <div className='blaze-text-muted'>
                  Categories can be added{' '}
                  <a
                    href={Routes.DASHBOARD_PRODUCTS_CATEGORIES(company!.id)}
                    target='_blank'
                    rel='noreferrer'
                  >
                    here
                  </a>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='d-flex'>
                  <Select<string | null>
                    showSearch
                    className='w-100 show-close-icon'
                    size='large'
                    dropdownMatchSelectWidth={false}
                    value={product.categoryId}
                    disabled={product.isDeleted}
                    allowClear={product.categoryId !== null}
                    onChange={(categoryId) => {
                      product.setCategoryId(categoryId || null)
                      this.autoEnableBatchTrack()
                    }}
                    filterOption={filterOption}
                  >
                    {flattenedCategories.map((category, index) => (
                      <Select.Option key={index} value={category.id}>
                        {getCategoryName(category)}
                      </Select.Option>
                    ))}
                  </Select>

                  <Icon
                    onClick={() => {
                      if (isRefreshing) {
                        return
                      }
                      this.updateCategories()
                    }}
                    type='sync'
                    spin={isRefreshing}
                    className='ml-3 align-self-center'
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </div>
              </div>
            </div>
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>Brand</label>
                <div className='blaze-text-muted'>
                  Brands can be added{' '}
                  <a
                    href={Routes.DASHBOARD_PRODUCTS_SUPPLIERS(company!.id)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    here
                  </a>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='d-flex'>
                  <Select<number | null>
                    showSearch
                    className='w-100 show-close-icon'
                    size='large'
                    dropdownMatchSelectWidth={false}
                    value={product.supplierId}
                    disabled={product.isDeleted}
                    allowClear={product.supplierId !== null}
                    onChange={(supplierId) => {
                      product.setSupplierId(supplierId || null)
                    }}
                    filterOption={filterOption}
                  >
                    {suppliers.map((supplier, index) => (
                      <Select.Option key={index} value={supplier.id}>
                        {supplier.name}
                      </Select.Option>
                    ))}
                  </Select>

                  <Icon
                    onClick={() => {
                      if (isRefreshing) {
                        return
                      }
                      this.updateSuppliers()
                    }}
                    type='sync'
                    spin={isRefreshing}
                    className='ml-3 align-self-center'
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </div>
              </div>
            </div>
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>Taxes</label>
                <div className='blaze-text-muted'>
                  Taxes can be configured{' '}
                  <a
                    href={Routes.DASHBOARD_SETTINGS_TAXES(company!.id)}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    here
                  </a>
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='d-flex'>
                  <Select<number | null>
                    showSearch
                    className='w-100 show-close-icon'
                    size='large'
                    dropdownMatchSelectWidth={false}
                    value={product.taxGroupId}
                    disabled={product.isDeleted || product.isGiftCard}
                    allowClear={product.taxGroupId !== null}
                    onChange={(taxGroupId) => {
                      product.setTaxGroupId(taxGroupId || null)
                    }}
                    filterOption={filterOption}
                  >
                    {taxGroups.map((taxGroup, index) => (
                      <Select.Option key={index} value={taxGroup.id}>
                        {taxGroup.name}
                      </Select.Option>
                    ))}
                  </Select>

                  <Icon
                    onClick={() => {
                      if (isRefreshing) {
                        return
                      }
                      this.updateTaxGroups()
                    }}
                    type='sync'
                    spin={isRefreshing}
                    className='ml-3 align-self-center'
                    style={{
                      cursor: 'pointer',
                    }}
                  />
                </div>
              </div>
            </div>
            {(company!.permissions.canAccessPricingGroups || product.pricingGroupId) && (
              <div className='form-group row card__body__text'>
                <div className='col-lg-4'>
                  <label>Pricing group </label>
                  <div className='blaze-text-muted'>
                    <a onClick={() => this.setState({ showPricingGroupHelpModal: true })}>
                      What's this?
                    </a>
                  </div>
                </div>
                <div className='col-lg-6'>
                  <div className='d-flex'>
                    <Select<string | null>
                      showSearch
                      className='w-100'
                      size='large'
                      dropdownMatchSelectWidth={false}
                      value={product.pricingGroupId}
                      disabled={(!!product.id && !product.isMasterProduct) || product.isDeleted}
                      allowClear
                      onChange={(pricingGroupId) => {
                        this.attachPricingGroup(pricingGroupId || null)
                      }}
                      filterOption={filterOption}
                    >
                      {pricingGroups.map((pricingGroup, index) => (
                        <Select.Option key={index} value={pricingGroup.id}>
                          {pricingGroup.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {!hasVariants && (
              <div className='form-group row card__body__text'>
                <div className='col-lg-4'>
                  <label>Receipt description</label>
                  <div className='blaze-text-muted'>Will show up on your receipts</div>
                </div>
                <div className='col-lg-6'>
                  <TextArea
                    value={product.shortDescription}
                    disabled={product.isDeleted}
                    onChange={(e) => product.setShortDescription(e.target.value)}
                  />
                </div>
              </div>
            )}
            <div className='form-group row card__body__text'>
              <div className='col-lg-4'>
                <label>Description</label>
                {hasVariants && (
                  <div className='blaze-text-muted'>Used for Leafly Integrations</div>
                )}
              </div>
              <div className='col-lg-6'>
                <TextArea
                  rows={3}
                  value={product.description}
                  disabled={product.isDeleted}
                  onChange={(e) => product.setDescription(e.target.value)}
                />
              </div>
            </div>

            {product.id && hasVariants && (
              <div className='form-group row card__body__text'>
                <div className='col-lg-4'>
                  <label>Edit history</label>
                </div>
                <div className='col-lg-8'>
                  {this.state.viewEditHistory ? (
                    <ProductEventLogs product={product} />
                  ) : (
                    <BlazeButton
                      buttonProps={{
                        onClick: () => this.setState({ viewEditHistory: true }),
                        className: 'mr-2 mb-2',
                      }}
                      titleProps={{
                        title: 'View edit history',
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          {showPricingGroupHelpModal && (
            <PricingGroupHelpModal
              onClose={() => this.setState({ showPricingGroupHelpModal: false })}
            />
          )}

          {showPricingGroupMigrationModal && (
            <PricingGroupMigrationModal
              product={product}
              pricingGroups={this.props.pricingGroups}
              onClose={this.onPricingGroupMigrationModalClose}
              onSave={() => this.setState({ showPricingGroupMigrationModal: false })}
            />
          )}
        </div>
      </>
    )
  }
}
